import type { Project } from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import { DropdownMenu } from "@repo/ui/Dropdown/dropdownMenu";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconDotsVertical } from "@tabler/icons-react";
import copy from "copy-to-clipboard";
import { type FC, useState } from "react";
import { Button, MenuTrigger } from "react-aria-components";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { DuplicateJobOpeningModal } from "./projectSettings/recruiting/duplicateJobOpeningModal.js";
import { updateProject } from "./projectsSlice.js";

interface RecruitingProjectMoreMenuProps {
  project: Project;
  onClickDeleteProject: () => void;
  onClickCsvImport: () => void;
  onClickCsvExport: () => void;
  onOpenSharingTemplatesModal: () => void;
  onClickRegenerateInterviewLink: () => void;
}

interface MenuItem {
  label: string;
  onClick: () => void;
}

export const RecruitingProjectMoreMenu: FC<RecruitingProjectMoreMenuProps> = ({
  onClickDeleteProject,
  onClickCsvImport,
  onClickCsvExport,
  project,
  onOpenSharingTemplatesModal,
  onClickRegenerateInterviewLink,
}) => {
  const dispatch = useAppDispatch();
  const [isDuplicateJobOpeningModalOpen, setIsDuplicateJobOpeningModalOpen] =
    useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState<boolean>(false);

  const teammateAuthorizationContext = useTeammateAuthorization();
  const teammateCanCreate = teammateAuthorizationContext.can(
    PermissionActions.CREATE,
    PermissionSubjects.JOB_OPENINGS,
  );
  const teammateCanDelete = teammateAuthorizationContext.can(
    PermissionActions.DELETE,
    PermissionSubjects.JOB_OPENINGS,
  );
  const teammateCanUpdate = teammateAuthorizationContext.can(
    PermissionActions.UPDATE,
    PermissionSubjects.JOB_OPENINGS,
  );

  const menuItems: MenuItem[] = [
    teammateCanUpdate
      ? {
          label: "Pause",
          onClick: () => {
            setIsPauseModalOpen(true);
          },
        }
      : null,
    {
      label: "View sharing templates",
      onClick: () => {
        onOpenSharingTemplatesModal();
      },
    },
    /* Don't support duplication for legacy projects without job types */
    teammateCanCreate && project.job_type
      ? {
          label: "Duplicate job opening",
          onClick: () => {
            setIsDuplicateJobOpeningModalOpen(true);
          },
        }
      : null,
    { label: "Import candidates from CSV", onClick: onClickCsvImport },
    { label: "Export interviews to CSV", onClick: onClickCsvExport },
    teammateCanUpdate
      ? {
          label: "Refresh interview link",
          onClick: () => {
            onClickRegenerateInterviewLink();
          },
        }
      : null,
    {
      label: "Copy job opening ID",
      onClick: () => {
        copy(project.id);
        showToast({
          title: `Job opening ID copied to clipboard`,
          variant: ToastVariant.Success,
        });
      },
    },
    teammateCanDelete
      ? {
          label: "Delete job opening",
          onClick: onClickDeleteProject,
        }
      : null,
  ].filter(isNotNullNotUndefined);

  return (
    <>
      <div className="relative ml-3">
        <MenuTrigger>
          <Button className="relative flex items-center rounded-md bg-white p-1.5 text-foreground shadow-xs ring-1 ring-border-strong ring-inset hover:bg-gray-50 disabled:bg-disabled disabled:text-muted-foreground disabled:shadow-none">
            <span className="absolute -inset-1.5" />
            <span className="sr-only">Open user menu</span>
            <IconDotsVertical className="h-6 w-6" />
          </Button>
          <DropdownMenu items={menuItems} />
        </MenuTrigger>
      </div>
      <SimpleModal
        isOpen={isPauseModalOpen}
        variant={SimpleModalVariant.Warning}
        title="Pause job opening"
        subtitle="If you pause this job opening, candidates will be blocked from interviewing but existing interviews will remain. You can relaunch this job opening at any time."
        confirmButtonText="Pause job opening"
        onCancel={() => {
          setIsPauseModalOpen(false);
        }}
        onConfirm={() =>
          void (async () => {
            try {
              await dispatch(
                updateProject({
                  projectId: project.id,
                  changes: { is_live: false },
                }),
              );

              showToast({
                title: `Job opening paused`,
                variant: ToastVariant.Success,
              });
            } catch (err) {
              console.error("error updating job opening", err);
              showToast({
                title: `Failed to pause job opening`,
                variant: ToastVariant.Error,
              });
            }

            setIsPauseModalOpen(false);
          })()
        }
      />
      {isDuplicateJobOpeningModalOpen ? (
        <DuplicateJobOpeningModal
          isOpen
          onClose={() => {
            setIsDuplicateJobOpeningModalOpen(false);
          }}
          project={project}
        />
      ) : null}
    </>
  );
};
